import React from "react";
import { FaUser, FaChartBar, FaBox, FaCalendarCheck, FaFileInvoiceDollar, FaEye } from "react-icons/fa";
import { MdEmail } from "react-icons/md";

// Add this function before the groupedProducts logic
const formatCategoryName = (category) => {
  if (!category) return "Uncategorized";
  
  // Special case for imGroup
  if (category.toLowerCase() === 'imgroup') {
    return 'IM Group';
  }
  
  // Convert camelCase to Title Case with spaces
  return category
    .replace(/([A-Z])/g, ' $1') // Add space before capital letters
    .replace(/^./, str => str.toUpperCase()) // Capitalize first letter
    .trim();
};

export default function UserActivityView({ userActivity }) {
  if (!userActivity) return null;

  // Group products by category and calculate totals
  const groupedProducts = userActivity.products?.reduce((acc, product) => {
   const category = formatCategoryName(product.category);
    if (!acc[category]) {
      acc[category] = {
        products: [],
        totalCount: 0,
        totalPrice: 0
      };
    }
    
    const productTotal = (product.price || 0) * (product.count || 0);
    acc[category].products.push(product);
    acc[category].totalCount += product.count || 0;
    acc[category].totalPrice += productTotal;
    
    return acc;
  }, {}) || {};

  // Calculate overall totals
  const overallTotals = Object.values(groupedProducts).reduce((acc, category) => {
    acc.totalCount += category.totalCount;
    acc.totalPrice += category.totalPrice;
    return acc;
  }, { totalCount: 0, totalPrice: 0 });

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(price);
  };

  return (
    <div className="bg-white">
      {/* Header */}
      <div className="bg-lightgray p-6 rounded-lg mb-6">
        <div className="flex items-center gap-3 mb-3">
          <FaUser className="text-darkergray text-xl" />
          <div>
            <div className="text-lg font-semibold text-black">
              {userActivity.user?.name}
            </div>
            <div className="flex items-center gap-2 text-darkergray">
              <MdEmail className="text-sm" />
              <span>{userActivity.user?.email}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="space-y-6">
        {/* Activity Summary */}
        <div>
          <div className="font-medium text-black mb-3 flex items-center gap-2">
            <FaChartBar className="text-green-600" />
            Activity Summary
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            {/* Price Processes */}
            <div className="bg-lightgray rounded-lg p-4 border-l-4 border-yellow-500">
              <div className="flex items-center gap-3 mb-2">
                <FaFileInvoiceDollar className="text-xl text-yellow-600" />
                <div>
                  <div className="text-sm text-darkgray mb-1">Price Processes</div>
                  <div className="text-2xl font-semibold text-black">
                    {userActivity.priceProcessCount || 0}
                  </div>
                </div>
              </div>
            </div>

            {/* Book Appraisals */}
            <div className="bg-lightgray rounded-lg p-4 border-l-4 border-green-500">
              <div className="flex items-center gap-3 mb-2">
                <FaEye className="text-xl text-green-600" />
                <div>
                  <div className="text-sm text-darkgray mb-1">Book Appraisals</div>
                  <div className="text-2xl font-semibold text-black">
                    {userActivity.bookAppraisalCount || 0}
                  </div>
                </div>
              </div>
            </div>

            {/* Auth Schedules */}
            <div className="bg-lightgray rounded-lg p-4 border-l-4 border-orange-500">
              <div className="flex items-center gap-3 mb-2">
                <FaCalendarCheck className="text-xl text-orange-600" />
                <div>
                  <div className="text-sm text-darkgray mb-1">Auth Schedules</div>
                  <div className="text-2xl font-semibold text-black">
                    {userActivity.authScheduleCount || 0}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Products Used by Category */}
        <div>
          <div className="font-medium text-black mb-3 flex items-center gap-2">
            <FaBox className="text-orange-600" />
            Products Used by Category
          </div>
          
          {Object.keys(groupedProducts).length > 0 ? (
            <div className="space-y-4">
              {Object.entries(groupedProducts).map(([category, categoryData]) => (
                <div key={category} className="bg-lightgray rounded-lg p-4">
                  {/* Category Header */}
                  <div className="flex justify-between items-center mb-3 pb-2 border-b border-darkgray">
                    <div className="font-semibold text-black text-lg">{category}</div>
                    <div className="text-right">
                      <div className="text-sm text-darkgray">
                        {categoryData.totalCount} {categoryData.totalCount === 1 ? 'item' : 'items'}
                      </div>
                      <div className="font-semibold text-orange-600">
                        {formatPrice(categoryData.totalPrice)}
                      </div>
                    </div>
                  </div>

                  {/* Products in Category */}
                  <div className="grid gap-2 md:grid-cols-2">
                    {categoryData.products.map((product, index) => (
                      <div
                        key={product.productId || index}
                        className="bg-white rounded p-3 border-l-4 border-orange-500"
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="font-medium text-black text-sm">
                              {product.shortName || product.name}
                            </div>
                            <div className="text-xs text-darkgray">
                              {formatPrice(product.price || 0)} each
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-semibold text-orange-600">
                              {product.count}x
                            </div>
                            <div className="text-xs text-black font-medium">
                              {formatPrice((product.price || 0) * (product.count || 0))}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {/* Overall Total */}
              <div className="bg-yellow-100 border border-yellow-300 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <div className="font-semibold text-black">
                    Overall Total ({overallTotals.totalCount} {overallTotals.totalCount === 1 ? 'item' : 'items'})
                  </div>
                  <div className="text-xl font-bold text-yellow-700">
                    {formatPrice(overallTotals.totalPrice)}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-lightgray rounded-lg p-6 flex flex-col gap-2 items-center justify-center">
              <FaBox className="text-4xl text-darkgray" />
              <p className="text-darkgray italic mt-2">
                No products used yet
              </p>
              <p className="text-darkgray text-sm">
                Products usage will appear here when user starts using them
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}